import { MutableRefObject, useEffect, useRef, useState, VoidFunctionComponent } from 'react';
import { Line, Text } from '@react-three/drei';
import { ThreeEvent, useFrame } from '@react-three/fiber';
import { MeshBasicMaterial, Path, Shape, Vector2 } from 'three';
import { helveticaNeue } from '@/lib/fonts';
import { PlaystationButton } from '@/components/PlaystationButton';
import { pathTo3D } from '@/lib/pathTo3D';
import { PINPAD_WIDTH } from '@/components/scenes/code-input/PinPad';
import { PULSE_PERIOD } from '@/lib/globals';
import selectionFocusSound from '@/sounds/ob-sfx-move-selection-focus.mp3';
import { oscillate } from '@/lib/mathUtils';
import { useOnlybotsSound } from '@/hooks/useOnlybotsSound';

const NODE_HEIGHT = 1.02;
const RADIUS = NODE_HEIGHT / 2;
const GAP_BETWEEN_NODES = 0.16;
const CONNECTOR_RADIUS = 0.22;
const OK_BG_MIN_OPACITY = 0.05;
const OK_BG_MAX_OPACITY = 0.75;

const okBgPoints = (): Vector2[] => {
    const left = 0.42;
    const right = 1.0;
    const radius = 0.44;
    const path = new Path();

    // draw line with rounded corners within bounds
    path.moveTo(left, -radius);
    path.absarc(left, 0, radius, (3 * Math.PI) / 2, Math.PI / 2, true);
    path.lineTo(right, radius);
    path.absarc(right, 0, radius, Math.PI / 2, (3 * Math.PI) / 2, true);
    path.lineTo(left, -radius);
    return path.getPoints();
};
const okOutline = pathTo3D(okBgPoints());
const okBgShape = new Shape();
okBgShape.setFromPoints(okBgPoints());

const drawMetaball = (): [number, number, number][] => {
    const bottom = -RADIUS;
    const top = RADIUS;
    const node1Left = RADIUS;
    const node1Width = 0.6;
    const node2Right = PINPAD_WIDTH - RADIUS;
    const node1Right = node1Left + node1Width;
    const node2Left = node1Left + node1Width + RADIUS + GAP_BETWEEN_NODES + RADIUS;
    const theta = Math.asin((RADIUS + GAP_BETWEEN_NODES / 2) / (RADIUS + CONNECTOR_RADIUS));
    const nodeConnectorCenterX = node1Right + (RADIUS + GAP_BETWEEN_NODES / 2);
    const nodeConnectorCenterY = (RADIUS + CONNECTOR_RADIUS) * Math.cos(theta);

    const path = new Path();
    // draw line with rounded corners within bounds
    path.moveTo(node1Left, bottom);
    path.absarc(node1Left, 0, RADIUS, (3 * Math.PI) / 2, Math.PI / 2, true);
    path.lineTo(node1Right, top);
    path.absarc(node1Right, 0, RADIUS, Math.PI / 2, Math.PI / 2 - theta, true);
    path.absarc(
        nodeConnectorCenterX,
        nodeConnectorCenterY,
        CONNECTOR_RADIUS,
        (3 * Math.PI) / 2 - theta,
        (3 * Math.PI) / 2 + theta,
        false
    );
    path.absarc(node2Left, 0, RADIUS, Math.PI / 2 + theta, Math.PI / 2, true);
    path.lineTo(node2Right, top);
    path.absarc(node2Right, 0, RADIUS, Math.PI / 2, (3 * Math.PI) / 2, true);
    path.lineTo(node2Left, bottom);
    path.absarc(node2Left, 0, RADIUS, (3 * Math.PI) / 2, (3 * Math.PI) / 2 - theta, true);
    path.absarc(
        nodeConnectorCenterX,
        -nodeConnectorCenterY,
        CONNECTOR_RADIUS,
        Math.PI / 2 - theta,
        Math.PI / 2 + theta,
        false
    );
    path.absarc(node1Right, 0, RADIUS, (3 * Math.PI) / 2 + theta, (3 * Math.PI) / 2, true);
    path.lineTo(node1Left, bottom);
    return pathTo3D(path.getPoints());
};
const metaballOutline = drawMetaball();

interface Props {
    onCancel: () => void;
    onOk: () => void;
}

export const OkCancelMetaball: VoidFunctionComponent<Props> = ({ onOk, onCancel }) => {
    const [playSelectionFocusSound] = useOnlybotsSound(selectionFocusSound);
    const [okHovered, setOkHovered] = useState<boolean>(false);
    const [cancelHovered, setCancelHovered] = useState<boolean>(false);
    const timer = useRef<number>(0);
    const bgRef = useRef() as MutableRefObject<MeshBasicMaterial>;
    const over =
        (setHovered: (hoverState: boolean) => void) =>
        (e: ThreeEvent<PointerEvent>): void => {
            e.stopPropagation();
            playSelectionFocusSound();
            setHovered(true);
        };
    const out = (setHovered: (hoverState: boolean) => void) => (): void => {
        setHovered(false);
        bgRef.current.opacity = OK_BG_MIN_OPACITY;
        timer.current = 0;
    };
    useEffect(() => {
        if (okHovered || cancelHovered) {
            document.body.style.cursor = 'pointer';
        }
        return () => {
            document.body.style.cursor = 'auto';
        };
    }, [okHovered, cancelHovered]);

    useFrame((_, delta) => {
        if (okHovered) {
            timer.current += delta;
            bgRef.current.opacity = oscillate(OK_BG_MIN_OPACITY, OK_BG_MAX_OPACITY, timer.current, PULSE_PERIOD);
        }
    });
    return (
        <>
            <group position={[0.1, 0.005, 0]}>
                <mesh onClick={onOk} onPointerOver={over(setOkHovered)} onPointerOut={out(setOkHovered)}>
                    <shapeGeometry attach="geometry" args={[okBgShape]} />
                    <meshBasicMaterial ref={bgRef} color="#e0e0e0" transparent opacity={OK_BG_MIN_OPACITY} />
                </mesh>
                <Line position={[0, 0, 0.01]} points={okOutline} color={0xc0b0b0} lineWidth={3} />
                <Text
                    position={[0.7, -0.05, 0]}
                    font={helveticaNeue}
                    fontSize={0.28}
                    color="white"
                    anchorX="center"
                    fillOpacity={0.6}
                    anchorY="middle"
                >
                    OK
                </Text>
            </group>
            <group
                position={[2.26, -0.03, 0]}
                scale={0.98}
                onPointerOver={over(setCancelHovered)}
                onPointerOut={out(setCancelHovered)}
            >
                <PlaystationButton
                    clickableAreaDimensions={[2.5, 1]}
                    icon="x"
                    label="CANCEL"
                    action={onCancel}
                    textPosition={[0.43, 0.13, 0]}
                />
            </group>
            <Line points={metaballOutline} color={0xe0d0d0} lineWidth={4} />
        </>
    );
};
